// Firestore access for the orders collection: idempotent upsert with the
// field-ownership merge policy (ORDER_SCHEMA.md §7), paginated/active queries,
// search, past-customer lookup, and workflow mutations.

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  onSnapshot,
  runTransaction,
  updateDoc,
  setDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import {
  ORDERS_COLLECTION,
  ORDERS_PAGE_SIZE,
  ACTIVE_LIMIT,
  HISTORY_PAGE_SIZE,
  DONE_STATUSES,
  REPEAT_VERDICT,
  STATUS,
  LINE_ITEM_STATUS_ORDER,
  isActiveStatus,
  rollUpStatus,
  effectiveStatus,
  isEmbroideryProduct,
} from '../constants';
import { finalizeOrder } from '../lib/order';

const ordersCol = () => collection(db, ORDERS_COLLECTION);
const orderRef = (docId) => doc(db, ORDERS_COLLECTION, docId);

/** Mint a fresh Firestore id (for manual "direct" orders with no platform id). */
export function newOrderId() {
  return doc(ordersCol()).id;
}

// Fields owned by our workflow — never overwritten by a platform re-import.
// (Listed for documentation; enforced by omission in buildExistingPatch.)

/** Convert a Firestore snapshot to an app order (Timestamps -> Dates). */
export function fromSnapshot(snap) {
  const d = snap.data();
  const toDate = (t) => (t && typeof t.toDate === 'function' ? t.toDate() : t || null);

  // Migration-on-read: legacy orders have an order-level status but no per-line
  // statuses. Seed each line from the stored status (if it's a line-type status),
  // and split a stored order-level status into manualStatus.
  const storedStatus = d.workflow?.status || STATUS.UNCUT;
  const isLineType = LINE_ITEM_STATUS_ORDER.includes(storedStatus);
  const seedLine = isLineType ? storedStatus : STATUS.UNCUT;
  const lineItems = (d.lineItems || []).map((li) => {
    const isEmb = li.isEmbroidery ?? isEmbroideryProduct(li.product);
    return { ...li, isEmbroidery: isEmb, status: li.status || (isEmb ? STATUS.PENDING : seedLine) };
  });
  const manualStatus =
    d.workflow?.manualStatus !== undefined ? d.workflow.manualStatus : isLineType ? null : storedStatus;
  const productionStatus = d.workflow?.productionStatus || rollUpStatus(lineItems);

  return {
    id: snap.id,
    ...d,
    lineItems,
    workflow: { ...d.workflow, manualStatus, productionStatus },
    dates: {
      ...d.dates,
      ordered: toDate(d.dates?.ordered),
      paid: toDate(d.dates?.paid),
      postByTarget: toDate(d.dates?.postByTarget),
      postByPlatform: toDate(d.dates?.postByPlatform),
      postByEffective: toDate(d.dates?.postByEffective),
    },
  };
}

// Build the merge patch for an EXISTING doc: platform facts overwrite; EG-owned
// workflow fields are omitted (preserved); shared fields fill only when empty;
// fields in `overriddenFields` (Kate's edits) are preserved and any differing
// incoming value is reported as an exception.
// Returns { patch, exceptions }.
function buildExistingPatch(full, existing) {
  // Protected paths come from the overrides map (preferred) plus any legacy array.
  const overrides = new Set([
    ...Object.keys(existing.overrides || {}),
    ...(existing.overriddenFields || []),
  ]);
  const exceptions = [];

  const patch = {
    source: full.source,
    legacyChannel: full.legacyChannel,
    platformOrderId: full.platformOrderId,
    platformOrderId2: full.platformOrderId2,
    compositeKey: full.compositeKey,
    buyer: { ...full.buyer },
    lineItems: full.lineItems,
    money: { ...full.money },
    raw: full.raw,
    // verdict/basis recomputed; confirmedByUser preserved (omitted).
    repeatCustomer: { verdict: full.repeatCustomer.verdict, basis: full.repeatCustomer.basis },
    // isLeather is platform-derived; status/posted/egNotes/sortKey/ftoc preserved (omitted).
    workflow: { isLeather: full.workflow.isLeather },
    meta: {
      manualEntry: existing.meta?.manualEntry ?? full.meta.manualEntry,
      importBatchId: full.meta.importBatchId,
      schemaVersion: 1,
      updatedAt: serverTimestamp(),
    },
  };

  const shipping = { ...full.shipping };

  // Honour Kate's overrides: drop the path from the patch (keep existing) and,
  // if the incoming platform value differs, record an exception.
  const protect = (path, group, key) => {
    if (!overrides.has(path)) return;
    const incoming = group[key];
    const kept = existingNested(existing, path);
    if (String(incoming ?? '') !== String(kept ?? '')) {
      exceptions.push({ path, kept, incoming });
    }
    delete group[key];
  };
  ['name', 'email'].forEach((k) => protect(`buyer.${k}`, patch.buyer, k));
  ['name', 'phone', 'address1', 'address2', 'townCity', 'county', 'postcode', 'country', 'service'].forEach((k) =>
    protect(`shipping.${k}`, shipping, k)
  );
  ['postage', 'discount', 'refund'].forEach((k) => protect(`money.${k}`, patch.money, k));

  // Line items: carry over each line's production status from the existing doc
  // (match by product/variation/sku) so a re-import never wipes cutting progress,
  // then recompute the header roll-up. If Kate has overridden line items, keep
  // hers entirely.
  if (overrides.has('lineItems')) {
    if (JSON.stringify(existing.lineItems || []) !== JSON.stringify(full.lineItems || [])) {
      exceptions.push({ path: 'lineItems', kept: 'your line items', incoming: 'platform line items' });
    }
    delete patch.lineItems; // existing line items + their derived status preserved
  } else {
    const carried = carryLineStatuses(full.lineItems, existing.lineItems);
    patch.lineItems = carried;
    const production = rollUpStatus(carried);
    const effective = effectiveStatus(existing.workflow?.manualStatus || null, carried);
    patch.workflow.productionStatus = production;
    patch.workflow.status = effective;
    patch.workflow.isActive = isActiveStatus(effective);
  }

  // dates: ordered/paid are platform facts; post-by is protect-if-already-set.
  const dates = { ordered: full.dates.ordered, paid: full.dates.paid };
  if (!existing.dates?.postByTarget) dates.postByTarget = full.dates.postByTarget;
  if (!existing.dates?.postByPlatform) dates.postByPlatform = full.dates.postByPlatform;
  if (!existing.dates?.postByEffective) dates.postByEffective = full.dates.postByEffective;
  patch.dates = dates;

  // shipping shared `service`: preserved if already set.
  if (existing.shipping?.service) delete shipping.service;
  patch.shipping = shipping;

  // shared trackingNumber: fill only if not already entered locally.
  if (!existing.workflow?.trackingNumber) {
    patch.workflow.trackingNumber = full.workflow.trackingNumber;
  }

  return { patch, exceptions };
}

// Read a dotted path from a nested object.
function existingNested(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

const lineSig = (li) =>
  `${(li.product || '').toLowerCase()}|${(li.variation || '').toLowerCase()}|${(li.sku || '').toLowerCase()}`;

// Copy each existing line's production status onto the matching incoming line
// (by product/variation/sku signature; consumes matches to handle duplicates).
function carryLineStatuses(incoming, existingItems) {
  const pool = (existingItems || []).map((li) => ({ key: lineSig(li), status: li.status }));
  return incoming.map((li) => {
    const idx = pool.findIndex((p) => p.key === lineSig(li));
    if (idx !== -1) {
      const { status } = pool[idx];
      pool.splice(idx, 1);
      return { ...li, status: status || li.status };
    }
    return li; // genuinely new line — keep its default (Uncut)
  });
}

/**
 * Idempotent upsert. New docs are written in full (workflow defaults applied);
 * existing docs are merged per the field-ownership policy.
 * Returns { outcome: 'created'|'updated', exceptions: [{path, kept, incoming}] }.
 */
export async function upsertOrder(core) {
  const full = finalizeOrder(core);
  const { docId, ...data } = full;
  const ref = orderRef(docId);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      tx.set(ref, {
        ...data,
        meta: { ...data.meta, importedAt: serverTimestamp(), updatedAt: serverTimestamp() },
      });
      return { outcome: 'created', exceptions: [] };
    }
    const { patch, exceptions } = buildExistingPatch(full, snap.data());
    tx.set(ref, patch, { merge: true });
    return { outcome: 'updated', exceptions };
  });
}

/**
 * Save manual edits to an existing order. The edited order is re-finalised
 * (totals/search-keys/derived flags recomputed) and written with merge.
 * `overrides` is a map { path: originalValue } of platform-owned fields Kate
 * changed — kept so a future platform re-import won't overwrite them, and so
 * the UI can show "was: <original>" on changed fields.
 */
export async function saveOrder(order, overrides = {}) {
  const full = finalizeOrder(order);
  const { docId, ...data } = full;
  const ref = orderRef(order.id || docId);
  const patch = {
    ...data,
    overrides, // merged map; keys accumulate (merge:true)
    meta: { ...data.meta, updatedAt: serverTimestamp() }, // importedAt preserved via merge
  };
  await setDoc(ref, patch, { merge: true });
}

/**
 * Set the header's manual order-level status override (or null = back to auto).
 * Effective status = override ?? production roll-up.
 */
export function setHeaderManualStatus(order, manualStatus) {
  const effective = effectiveStatus(manualStatus || null, order.lineItems || []);
  return updateDoc(orderRef(order.id), {
    'workflow.manualStatus': manualStatus || null,
    'workflow.status': effective,
    'workflow.isActive': isActiveStatus(effective),
    'meta.updatedAt': serverTimestamp(),
  });
}

// Recompute derived header fields from a new line-items array and write both.
// `clearManual` also drops the manual override (used when advancing production
// on a single-line order that currently has an override set).
function writeLineItems(order, lineItems, { clearManual = false } = {}) {
  const manual = clearManual ? null : order.workflow?.manualStatus || null;
  const production = rollUpStatus(lineItems);
  const effective = effectiveStatus(manual, lineItems);
  const patch = {
    lineItems,
    'workflow.productionStatus': production,
    'workflow.status': effective,
    'workflow.isActive': isActiveStatus(effective),
    'meta.updatedAt': serverTimestamp(),
  };
  if (clearManual) patch['workflow.manualStatus'] = null;
  return updateDoc(orderRef(order.id), patch);
}

/** Set one line item's production status (recomputes the header roll-up). */
export function setLineItemStatus(order, lineId, status, opts) {
  const lineItems = (order.lineItems || []).map((li) =>
    li.lineId === lineId ? { ...li, status } : li
  );
  return writeLineItems(order, lineItems, opts);
}

/** Bulk-set every line item to a status (e.g. "mark whole order Cut"). */
export function setAllLineItemStatuses(order, status) {
  const lineItems = (order.lineItems || []).map((li) => ({ ...li, status }));
  return writeLineItems(order, lineItems);
}

/** Patch arbitrary workflow fields (tracking number, posted, notes, ftoc). */
export function setWorkflowFields(docId, fields) {
  const patch = { 'meta.updatedAt': serverTimestamp() };
  Object.entries(fields).forEach(([k, v]) => {
    patch[`workflow.${k}`] = v;
  });
  return updateDoc(orderRef(docId), patch);
}

/** Persist a new manual sort position. */
export function setSortKey(docId, sortKey) {
  return updateDoc(orderRef(docId), {
    'workflow.sortKey': sortKey,
    'meta.updatedAt': serverTimestamp(),
  });
}

/**
 * Persist new sort positions for several orders at once (manual reorder).
 * `updates` = [{ id, sortKey }]. Batched; tolerant of >500 via chunking.
 */
export async function setSortKeys(updates) {
  for (let i = 0; i < updates.length; i += 450) {
    const chunk = updates.slice(i, i + 450);
    const batch = writeBatch(db);
    chunk.forEach(({ id, sortKey }) => batch.update(orderRef(id), { 'workflow.sortKey': sortKey }));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
}

/** Settle a "maybe" past-customer verdict. */
export function setRepeatConfirmed(docId, confirmed) {
  return updateDoc(orderRef(docId), {
    'repeatCustomer.confirmedByUser': confirmed,
    'meta.updatedAt': serverTimestamp(),
  });
}

/**
 * Live subscription to the active set (status not done), ordered by manual
 * sortKey. Calls onData(orders[]) on every change; returns the unsubscribe fn.
 */
export function listenActive(onData, onError, pageSize = ACTIVE_LIMIT) {
  const q = query(
    ordersCol(),
    where('workflow.isActive', '==', true),
    orderBy('workflow.sortKey', 'asc'),
    limit(pageSize)
  );
  return onSnapshot(
    q,
    (qs) => onData(qs.docs.map(fromSnapshot)),
    (err) => onError && onError(err)
  );
}

/** One-shot page of orders for a given status, ordered by post-by (soonest first). */
export async function getOrdersByStatus(status, { cursor, pageSize = HISTORY_PAGE_SIZE } = {}) {
  const parts = [
    ordersCol(),
    where('workflow.status', '==', status),
    orderBy('dates.postByEffective', 'asc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(pageSize),
  ];
  const qs = await getDocs(query(...parts));
  return { orders: qs.docs.map(fromSnapshot), lastDoc: qs.docs[qs.docs.length - 1] || null };
}

/** One-shot page across all orders (history), newest first. */
export async function getOrdersPage({ cursor, pageSize = HISTORY_PAGE_SIZE } = {}) {
  const parts = [
    ordersCol(),
    orderBy('dates.ordered', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(pageSize),
  ];
  const qs = await getDocs(query(...parts));
  return { orders: qs.docs.map(fromSnapshot), lastDoc: qs.docs[qs.docs.length - 1] || null };
}

const lower = (v) => String(v || '').trim().toLowerCase();

/** Prefix search on buyer name (case-insensitive). */
export async function searchByBuyerName(prefix, pageSize = ORDERS_PAGE_SIZE) {
  const p = lower(prefix);
  if (!p) return [];
  const qs = await getDocs(
    query(
      ordersCol(),
      orderBy('buyer.nameLower'),
      where('buyer.nameLower', '>=', p),
      where('buyer.nameLower', '<=', `${p}`),
      limit(pageSize)
    )
  );
  return qs.docs.map(fromSnapshot);
}

/** Exact match on platform user id. */
export async function searchByUserId(userId, pageSize = ORDERS_PAGE_SIZE) {
  if (!userId) return [];
  const qs = await getDocs(
    query(ordersCol(), where('buyer.userId', '==', String(userId).trim()), limit(pageSize))
  );
  return qs.docs.map(fromSnapshot);
}

/**
 * Past-customer detection. Returns { verdict, basis } where:
 *  - yes  : same platform userId (or email) has a prior order
 *  - maybe: same buyer name OR same first address line
 *  - no   : no match
 * `excludeDocId` skips the order being assessed.
 */

export async function detectRepeatCustomer({ userId, email, name, address1, excludeDocId }) {
  const basis = [];

  const anyOther = (snap) => snap.docs.some((d) => d.id !== excludeDocId);

  // Strong signals -> "yes"
  if (userId) {
    const qs = await getDocs(query(ordersCol(), where('buyer.userId', '==', userId), limit(2)));
    if (anyOther(qs)) basis.push('userId');
  }
  if (!basis.length && email) {
    const qs = await getDocs(
      query(ordersCol(), where('buyer.emailLower', '==', lower(email)), limit(2))
    );
    if (anyOther(qs)) basis.push('email');
  }
  if (basis.length) return { verdict: REPEAT_VERDICT.YES, basis };

  // Weak signals -> "maybe"
  if (name) {
    const qs = await getDocs(
      query(ordersCol(), where('buyer.nameLower', '==', lower(name)), limit(2))
    );
    if (anyOther(qs)) basis.push('name');
  }
  if (address1) {
    const qs = await getDocs(
      query(ordersCol(), where('shipping.address1Lower', '==', lower(address1)), limit(2))
    );
    if (anyOther(qs)) basis.push('address');
  }
  if (basis.length) return { verdict: REPEAT_VERDICT.MAYBE, basis };

  return { verdict: REPEAT_VERDICT.NO, basis: [] };
}

export { DONE_STATUSES };
